import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaNewspaper, FaEye, FaEdit, FaPlus } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';
import api from '../../../services/api';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  featured_image_url: string;
  author_name: string;
  category: string;
  views: number;
  published_at: string;
}

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useCloudflareAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [myArticles, setMyArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my'>('all');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const [allResponse, myResponse] = await Promise.all([
          api.getArticles({ limit: 50 }),
          currentUser ? api.getMyArticles() : Promise.resolve({ articles: [] })
        ]);
        setArticles(allResponse.articles || []);
        setMyArticles(myResponse.articles || []);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchArticles();
    }
  }, [currentUser, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  const displayArticles = filter === 'all' ? articles : myArticles;

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            >
              <FaArrowLeft className="text-slate-400" />
            </button>
            <h1 className="text-2xl font-bold text-emerald-400">Articles</h1>
          </div>
          <Link
            to="/dashboard/articles/new"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            <FaPlus /> New Article
          </Link>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All Articles ({articles.length})
          </button>
          <button
            onClick={() => setFilter('my')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'my' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            My Articles ({myArticles.length})
          </button>
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : displayArticles.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <FaNewspaper className="w-10 h-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {filter === 'my' ? 'No Articles Yet' : 'No Articles Available'}
            </h2>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              {filter === 'my' 
                ? 'You haven\'t written any articles yet. Click "New Article" to get started!'
                : 'No articles have been published yet. Check back soon!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayArticles.map((article) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-800 rounded-xl overflow-hidden hover:bg-slate-700/80 transition-colors"
              >
                {article.featured_image_url && (
                  <img
                    src={article.featured_image_url}
                    alt={article.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>{article.author_name}</span>
                    <div className="flex items-center gap-1">
                      <FaEye className="w-4 h-4" />
                      <span>{article.views}</span>
                    </div>
                  </div>
                  {filter === 'my' && (
                    <Link
                      to={`/dashboard/articles/edit/${article.id}`}
                      className="mt-4 flex items-center justify-center gap-2 w-full py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      <FaEdit /> Edit
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Articles;
